// Human-readable views of show-document scenes, for the board and details panel.

// Folders are colored by what kind of moment they are: the games one blue, the show telling you something
// (Zed's transmissions, the scores) orange, Zed's own moment red, sizzles pink, chat its own color.
const GAME = '#3d7bff', INFO = '#ff9516';
const GAME_COLORS = {
    Dancelestrations: GAME, ShowDancelestrations: GAME, DancelestrationsScores: GAME, DancelestrationsHighScores: GAME,
    Trilemma: GAME, TrilemmaScores: GAME, AllegedlyTrue: GAME, MakeItADanceRGB: GAME, MakeItADanceRainbow: GAME,
    AiDialog: INFO, Leaderboard: INFO, DevilsBargain: '#d4002a',
    Sizzler: '#ff4fc8', Chat: '#a679ff', Ready: '#9aa0a6', CharacterReady: '#9aa0a6',
};
const STRUCTURAL = new Set(['Blank', 'TextScene', 'DanceText', 'InstructionsScene', 'TitleVideo', 'GetReady']);

// A block that only introduces a game (preset + title video) belongs to that game.
const TITLE_VIDEO_GAMES = {dancelestrations: 'Dancelestrations', trilemma: 'Trilemma', 'allegedly-true': 'AllegedlyTrue', 'make-it-a-dance': 'MakeItADanceRGB'};
const gameComponent = (block) => {
    const direct = block.scenes.map(s => s.el.$el).find(c => GAME_COLORS[c]);
    if (direct) return direct;
    const video = block.scenes.find(s => s.el.$el === 'TitleVideo');
    return video ? TITLE_VIDEO_GAMES[(video.el.props || {}).name] : undefined;
};

// A scene's thumbnail is named after what the scene is, not where it sits, so editing the show never shifts
// them (tools/preview/thumbs.cjs computes the same name).
export const sceneKey = (scene) => {
    const str = JSON.stringify(scene.el);
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return h.toString(16).padStart(8, '0');
};

export const blockColor = (block) => {
    if (block.color) return block.color;          // set in the show (sections like Preface, Get Ready, Start)
    const game = gameComponent(block);
    return game ? GAME_COLORS[game] : '#5f6368';
};

export const blockGame = (block) => block.scenes.map(s => s.el.$el).find(c => !STRUCTURAL.has(c)) || block.scenes[0].el.$el;

const NAMES = {
    AiDialog: 'Zed Transmission', JFD3Title: 'JFD Title', PartyTechLogo: 'PartyTech Logo', DevilsBargain: 'The Deal', AllegedlyTrue: 'True or False', Ending: 'Recap',
    ShowDancelestrations: 'Results', DancelestrationsScores: 'Scores', DancelestrationsHighScores: 'High Scores',
    TrilemmaScores: 'Scores', MakeItADanceRainbow: 'Everybody Dances',
};
// What players are doing in each Dancelestrations round (the drawing ↔ dance telephone).
const DANCELESTRATIONS_ROUNDS = ['Draw', 'Guess the Drawing', 'Record a Dance', 'Guess the Dance', 'Draw the Dance'];
const words = (s) => String(s).replace(/[-_]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/\b[a-z]/g, c => c.toUpperCase());
const quote = (v) => {
    const t = (Array.isArray(v) ? v.join(' ') : typeof v === 'string' ? v : '').replace(/\s+/g, ' ').trim();
    if (!t) return '“…”';
    return `“${/[a-z]/.test(t) || !/[A-Z]/.test(t) ? t : t.toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase())}”`;
};

// The scene's title as the board and headline show it: capitalized words, no code names or stray values.
export const sceneLabel = (scene) => {
    const {$el, props = {}} = scene.el;
    switch ($el) {
        case 'TextScene': return quote(props.text);
        case 'DanceText': return quote(props.text);
        case 'InstructionsScene': return quote(props.lines);
        case 'TitleVideo': return TITLE_VIDEO_GAMES[props.name] ? 'Title Video' : words(props.name || 'Video');
        case 'Sizzler': return `Reel ${props.num}`;
        case 'Dancelestrations': return props.showPair ? 'Find Your Partner' : DANCELESTRATIONS_ROUNDS[props.round] || `Round ${props.round}`;
        case 'Trilemma': return props.finale ? 'Finale' : props.melee ? 'Melee' : `Game ${props.gameNumber}`;
        case 'MakeItADanceRGB': return props.round === 0 ? "Zed's Turn" : `Spotlight ${props.round}`;
        case 'Chat': return !props.room || props.room === 'general' ? 'Open Chat' : `${words(props.room)} Chat`;
        case 'Leaderboard': return props.type === 'end' ? 'Final Standings' : 'Standings';
        case 'PrepareLeaderboard': return 'Tallying Scores';
        default: return NAMES[$el] || words($el);
    }
};

export const elementNames = (v, out = []) => {
    if (Array.isArray(v)) v.forEach(x => elementNames(x, out));
    else if (v && typeof v === 'object') {
        if (v.$el) out.push(v.$el);
        Object.values(v.props || (v.$el ? {} : v)).forEach(x => elementNames(x, out));
    }
    return out;
};

// The light color a scene asks for today (mirrors how the stage visuals color themselves).
export const sceneColor = (scene) => {
    const p = scene.el.props || {};
    if (typeof p.r === 'number') return `rgb(${p.r},${p.g},${p.b})`;
    if (typeof p.hex === 'string' && p.hex !== '#000000') return p.hex;
    if (Array.isArray(p.rgb)) return `rgb(${p.rgb.join(',')})`;
    return null;
};

export const flatIndex = (doc, blockIndex, sceneIndex) =>
    doc.blocks.slice(0, blockIndex).reduce((n, b) => n + b.scenes.length, 0) + sceneIndex;

export const locate = (doc, index) => {
    let n = index;
    for (let b = 0; b < doc.blocks.length; b++) {
        if (n < doc.blocks[b].scenes.length) return {blockIndex: b, sceneIndex: n};
        n -= doc.blocks[b].scenes.length;
    }
    return null;
};

const GAME_TITLES = {
    Dancelestrations: 'Dancelestrations', ShowDancelestrations: 'Dancelestrations', DancelestrationsScores: 'Dancelestrations',
    Trilemma: 'Trilemma', TrilemmaScores: 'Trilemma', AllegedlyTrue: 'Allegedly True', DevilsBargain: "Devil's Bargain",
    MakeItADanceRGB: 'Make It A Dance', MakeItADanceRainbow: 'Make It A Dance', AiDialog: 'Intro',
    Leaderboard: 'Leaderboard', Sizzler: 'Sizzle', Chat: 'Chat',
};
const titleCase = (s) => s.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// A section = consecutive blocks belonging to the same game (Trilemma intro + melee + finale), or a lone block.
export const sections = (doc) => {
    const out = [];
    doc.blocks.forEach((block, b) => {
        const game = GAME_TITLES[gameComponent(block)] || null;
        const prev = out[out.length - 1];
        if (prev && prev.game === game && (game || prev.color === blockColor(block))) prev.blocks.push(b);
        else out.push({game, color: blockColor(block), blocks: [b]});
    });
    // Runs of non-game blocks read as one folder: "Doors → JFD", unless the first block names its section.
    return out.map(sec => ({...sec, title: sec.game || doc.blocks[sec.blocks[0]].section || (sec.blocks.length > 1
        ? `${titleCase(doc.blocks[sec.blocks[0]].label)} → ${titleCase(doc.blocks[sec.blocks.at(-1)].label)}`
        : titleCase(doc.blocks[sec.blocks[0]].label))}));
};

export const sectionOf = (doc, blockIndex) => sections(doc).find(sec => sec.blocks.includes(blockIndex));

// Readable text on a solid block color.
export const inkOn = (hex) => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
    if (!m) return '#0b0c0f';
    const [r, g, b] = m.slice(1).map(x => parseInt(x, 16) / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.55 ? '#0b0c0f' : '#ffffff';
};
